import {
  Badge, Button, Card, DataTable, DateInput, EmptyState, Field, LARGEST_PAGE, Notice, PageHeader, Row, Select,
  useApi, useClampedPage, useI18n, type Column, type PlatformAuditEntryResponse,
} from '@daghfal/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

const ACTIONS = ['Insert', 'Update', 'Delete'];

/**
 * The platform-wide audit trail (FR-PLT-08).
 *
 * The same trail a tenant sees of its own activity, read across every tenant at once. With one difference
 * the screen states outright: the values a record held before a change are not shown here. They would carry
 * children's names, which no platform permission reaches (FR-PLT-04) — reading those means raising a support
 * session that the tenant approves.
 *
 * The support-session filter is offered first among the toggles because those are the entries most worth
 * reading: a platform employee acting inside a customer's data with that customer's time-boxed approval.
 */
export function Audit() {
  const api = useApi();
  const { t, formatDateTime } = useI18n();

  const [tenantId, setTenantId] = useState('');
  const [entityTypeId, setEntityTypeId] = useState('');
  const [action, setAction] = useState('');
  const [supportOnly, setSupportOnly] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const reset = <T,>(setter: (value: T) => void) => (value: T) => { setter(value); setPage(1); };

  const tenants = useQuery({
    queryKey: ['platform', 'tenants', 'all-for-filter'],
    queryFn: () => api.platform.listTenants({ pageSize: LARGEST_PAGE }),
    staleTime: 5 * 60 * 1000,
  });

  const entityTypes = useQuery({
    queryKey: ['platform', 'audit', 'entities'],
    queryFn: () => api.platform.auditEntityTypes(),
    // Seeded with the product; they do not change while the console is open.
    staleTime: Infinity,
  });

  const entries = useQuery({
    queryKey: ['platform', 'audit', { tenantId, entityTypeId, action, supportOnly, from, to, page }],
    queryFn: () => api.platform.auditLog({
      tenantId: tenantId === '' ? undefined : Number(tenantId),
      entityTypeId: entityTypeId === '' ? undefined : Number(entityTypeId),
      action: action === '' ? undefined : action,
      supportSessionsOnly: supportOnly ? true : undefined,
      from: from === '' ? undefined : new Date(from).toISOString(),
      to: to === '' ? undefined : new Date(to).toISOString(),
      page,
      pageSize: 50,
    }),
    placeholderData: keepPreviousData,
  });
  useClampedPage(page, setPage, entries.data?.totalPages);

  const columns: Column<PlatformAuditEntryResponse>[] = [
    { key: 'at', header: t('audit.column.at'), render: (entry) => formatDateTime(entry.at) },
    {
      key: 'tenant',
      header: t('audit.column.tenant'),
      render: (entry) => entry.tenantName ?? <span className="dg-muted">{t('audit.platformScope')}</span>,
    },
    {
      key: 'actor',
      header: t('audit.column.actor'),
      render: (entry) => (
        <>
          {entry.actorName ?? <span className="dg-muted">{t('audit.system')}</span>}
          {entry.underSupportSession && (
            <> <Badge tone="warning">{t('audit.supportSession')}</Badge></>
          )}
        </>
      ),
    },
    {
      key: 'action',
      header: t('audit.column.action'),
      render: (entry) => t(`audit.action.${entry.action}`),
    },
    {
      key: 'entity',
      header: t('audit.column.entity'),
      render: (entry) => (
        <>
          {entry.entityName ?? '—'}
          {entry.entityId != null && <span className="dg-muted"> #{entry.entityId}</span>}
        </>
      ),
    },
    {
      key: 'endpoint',
      header: t('audit.column.endpoint'),
      render: (entry) => entry.endpoint ?? <span className="dg-muted">—</span>,
    },
  ];

  return (
    <>
      <PageHeader title={t('audit.title')} lede={t('audit.lede')} />

      <Notice tone="info" title={t('audit.privacy.title')}>{t('audit.privacy.body')}</Notice>

      <Card>
        <Row>
          <Field label={t('audit.column.tenant')}>
            {(props) => (
              <Select {...props} value={tenantId} onChange={(event) => reset(setTenantId)(event.target.value)}>
                <option value="">{t('audit.filter.allTenants')}</option>
                {(tenants.data?.items ?? []).map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{tenant.nameAr}</option>
                ))}
              </Select>
            )}
          </Field>

          <Field label={t('audit.column.entity')}>
            {(props) => (
              <Select
                {...props}
                value={entityTypeId}
                onChange={(event) => reset(setEntityTypeId)(event.target.value)}
              >
                <option value="">{t('audit.filter.allEntities')}</option>
                {(entityTypes.data ?? []).map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.code}</option>
                ))}
              </Select>
            )}
          </Field>

          <Field label={t('audit.column.action')}>
            {(props) => (
              <Select {...props} value={action} onChange={(event) => reset(setAction)(event.target.value)}>
                <option value="">{t('audit.filter.allActions')}</option>
                {ACTIONS.map((option) => (
                  <option key={option} value={option}>{t(`audit.action.${option}`)}</option>
                ))}
              </Select>
            )}
          </Field>

          <Field label={t('audit.filter.from')}>
            {(props) => (
              <DateInput {...props} value={from} onChange={reset(setFrom)} />
            )}
          </Field>

          <Field label={t('audit.filter.to')}>
            {(props) => (
              <DateInput {...props} value={to} onChange={reset(setTo)} />
            )}
          </Field>
        </Row>

        <Row>
          <label className="dg-row" style={{ gap: 'var(--dg-space-2)' }}>
            <input
              type="checkbox"
              checked={supportOnly}
              onChange={(event) => reset(setSupportOnly)(event.target.checked)}
            />
            <span>{t('audit.filter.supportOnly')}</span>
          </label>
        </Row>
      </Card>

      <DataTable
        caption={t('audit.caption')}
        columns={columns}
        rows={entries.data?.items}
        rowKey={(entry) => entry.id}
        isLoading={entries.isPending}
        error={entries.error}
        onRetry={() => void entries.refetch()}
        empty={<EmptyState icon="🧾" title={t('audit.empty')} body={t('audit.empty.body')} />}
      />

      {entries.data && entries.data.totalPages > 1 && (
        <Row between>
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>
            {t('action.previous')}
          </Button>
          <span>{page} / {entries.data.totalPages}</span>
          <Button
            variant="secondary"
            disabled={page >= entries.data.totalPages}
            onClick={() => setPage((n) => n + 1)}
          >
            {t('action.next')}
          </Button>
        </Row>
      )}
    </>
  );
}
